import { useCallback, useEffect, useRef, type ReactNode } from 'react'

import { RecordReachProvider, type RecordReachLookup } from '@/shared/access'
import { parseCursorPage } from '@/shared/api/paging'
import { useApiClient } from '@/shared/api/use-api-client'

import { parseCourseListing } from '../search/course-search'

import { courseRoute, coursesApi } from './paths'

/**
 * Links a course's name only for a caller who may open it: Find Courses, asked for the linked ids
 * without the courses only published to the caller, answers with those in their course read reach,
 * as Course Details does.
 */
export function CourseReachProvider(props: { readonly children: ReactNode }) {
  const client = useApiClient()
  // Kept current without changing the lookup, which would ask again: the client changes when sign-in settles.
  const api = useRef(client)
  useEffect(() => {
    api.current = client
  }, [client])

  const lookup = useCallback<RecordReachLookup>(async (ids) => {
    const query = new URLSearchParams({ ids: ids.join(','), includePublished: 'false', limit: String(ids.length) })
    const found = parseCursorPage(await api.current.get(`${coursesApi}?${query.toString()}`), parseCourseListing)
    return new Set(found.data.map((course) => course.id))
  }, [])

  return (
    <RecordReachProvider route={courseRoute} lookup={lookup}>
      {props.children}
    </RecordReachProvider>
  )
}
